/**CORE LIBRARIES IMPORT */
import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS IMPORT */
import EventPoster from "../Components/EventPoster";
import EventEntertainment from "../Components/EventEntertainment";
import PartnerLockup from "../Components/PartnerLockup";
import TurkishEmblem from "../Components/TurkishEmblem";
import SectionHeading from "../Components/SectionHeading";
import EventDrinks from "../Components/EventDrinks";
import NotFound from "./NotFound";

/**DATA / HOOKS / UTILS IMPORT */
import { getEventBySlug, formatEventDate, formatPrice } from "../data/events";
import { samplePerformers } from "../data/events.sample";
import { TBC } from "../data/site";
import useSiteSettings from "../hooks/useSiteSettings";
import { PRIVACY_PATH, REFUND_POLICY_PATH, TERMS_PATH } from "../data/legal";
import usePageTitle from "../hooks/usePageTitle";
import { useEventContent } from "../hooks/useContent";
import useEventAvailability, { bookingPathFor, availabilityLabel } from "../hooks/useEventAvailability";
import { ease, fadeUp, stagger, revealOnScroll } from "../utils/motion";

const MotionLink = motion.create(Link);

const EventDetails = () => {
  const settings = useSiteSettings();
  const contactEmail = settings.email;
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Static content, replaced by portal-managed content when the backend is live.
  const event = useEventContent(getEventBySlug(slug));
  usePageTitle(event ? event.title : "Event not found");
  const { availability } = useEventAvailability(event);

  if (!event) return <NotFound />;

  // Layout preview with labelled sample performers: dev server only.
  const showSamples =
    process.env.NODE_ENV === "development" && searchParams.get("sample") === "1";
  const entertainment = event.entertainment && showSamples
    ? { ...event.entertainment, performers: samplePerformers }
    : event.entertainment;

  const bookPath = bookingPathFor(event, availability);
  const isRepublicDay = event.theme === "turkish-republic-day";

  const details = [
    { label: "Date", value: formatEventDate(event.date) },
    { label: "Time", value: event.startTime || TBC },
    { label: "Venue", value: <span className="address-text">{settings.address}</span> },
    { label: "Price", value: `${formatPrice(event.price)} ${event.price.basis}` },
    { label: "Drinks", value: event.drinks ? `${event.price.note}; served separately by ${event.drinks.servedBy}` : event.price.note },
    { label: "Availability", value: availabilityLabel(availability) || event.availability || TBC },
  ];

  return (
    <main className="event-page">
      {/**PAGE HERO */}
      <section className={`page-hero ${isRepublicDay ? "page-hero-republic" : ""}`} aria-labelledby="event-title">
        <motion.div
          className="light-sweep"
          aria-hidden="true"
          animate={{ x: ["-100%", "100%"] }}
          transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
        />
        {isRepublicDay && <TurkishEmblem className="page-hero-emblem" />}

        <motion.div className="page-hero-inner" initial="hidden" animate="visible" variants={stagger(0.14)}>
          {event.partners && (
            <motion.div variants={fadeUp}>
              <PartnerLockup partners={event.partners} label={event.presentedBy} />
            </motion.div>
          )}
          <motion.p className="page-eyebrow" variants={fadeUp}>Special Event</motion.p>
          <motion.h1 id="event-title" className="page-title" variants={fadeUp}>
            {event.title}
          </motion.h1>
          <motion.p className="page-subtitle" variants={fadeUp}>
            {formatEventDate(event.date)}
            {event.startTime && <> · {event.startTime}</>} · {formatPrice(event.price)} {event.price.basis}
          </motion.p>
          <motion.div className="event-actions" variants={fadeUp}>
            <MotionLink to={bookPath} className="hero-btn hero-btn-active" whileHover={{ y: -6, scale: 1.05 }} whileTap={{ scale: 0.96 }}>
              Book Now
            </MotionLink>
          </motion.div>
        </motion.div>
      </section>

      {/**DETAILS */}
      <section className="event-details">
        <motion.div className="event-details-main" variants={stagger(0.15)} {...revealOnScroll(0.15)}>
          <motion.p className="section-text event-description" variants={fadeUp}>
            {event.description}
          </motion.p>

          <motion.div className="menu-card" variants={fadeUp}>
            <SectionHeading title="Event Menu" />

            <h3 className="menu-course">Starters</h3>
            <ul className="menu-list">
              {event.menu.starters.map((dish) => (
                <li key={dish}>{dish}</li>
              ))}
            </ul>

            <h3 className="menu-course">Main course</h3>
            <ul className="menu-list">
              {event.menu.main.map((dish) => (
                <li key={dish}>{dish}</li>
              ))}
            </ul>

            <p className="menu-price">
              <strong>{formatPrice(event.price)}</strong> {event.price.basis}
              <span> · {event.price.note}</span>
            </p>
          </motion.div>

          {event.drinks && (
            <motion.div className="menu-card drinks-card" variants={fadeUp}>
              <SectionHeading title="Drinks" />
              <EventDrinks drinks={event.drinks} currency={event.price.currency} />
            </motion.div>
          )}

          <motion.div variants={fadeUp}>
            <EventEntertainment entertainment={entertainment} sampleNotice={showSamples} />
          </motion.div>

          <motion.div variants={fadeUp}>
            <EventPoster event={event} />
          </motion.div>
        </motion.div>

        <motion.aside
          className="event-details-side"
          aria-label="Event details"
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease }}
        >
          <dl className="details-list">
            {details.map(({ label, value }) => (
              <div key={label} className="details-row">
                <dt>{label}</dt>
                <dd className={value === TBC ? "is-tbc" : undefined}>{value}</dd>
              </div>
            ))}
          </dl>

          <MotionLink to={bookPath} className="hero-btn hero-btn-active hero-btn-block" whileHover={{ y: -4 }} whileTap={{ scale: 0.97 }}>
            Book Now
          </MotionLink>
          <p className="details-note">
            Prefer email? Write to{" "}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </p>
          <p className="details-note">
            {availability?.on_sale && !availability.simulated
              ? "Tickets are paid securely online through SumUp. Your booking is confirmed once payment is verified."
              : "No payment is taken online at this time."}
          </p>
          <p className="details-note policy-links">
            <Link to={TERMS_PATH}>Terms &amp; Conditions</Link> ·{" "}
            <Link to={REFUND_POLICY_PATH}>Refund &amp; Cancellation Policy</Link> ·{" "}
            <Link to={PRIVACY_PATH}>Privacy Policy</Link>
          </p>
        </motion.aside>
      </section>
    </main>
  );
};

export default EventDetails;
